import React, { useState } from 'react';
import { Button, Modal, Select, Typography, message } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import esLocale from 'i18n-iso-countries/langs/es.json';
import 'flag-icons/css/flag-icons.min.css';
import updateUserSettings from '../../utils/user/updateUserSettings';
import { toast } from 'react-hot-toast';
import LinkButton from '../buttons/LinkButton';
import { getCurrentUser } from '../../utils/userStorage';

const { Option } = Select;
const { Text } = Typography;

countries.registerLocale(enLocale);
countries.registerLocale(esLocale);

const CallsCountrySelector = ({ onCountrySelected, asLink = false }) => {
  const { t } = useTranslation();
  const currentUser = getCurrentUser();

  // Extract the base language
  const userLanguage = i18n.language.split('-')[0];
  const countryNamesLanguage = userLanguage === 'es' ? 'es' : 'en';

  // Get names only for supported countries
  const countryOptions = Object.entries(countries.getNames(countryNamesLanguage, { select: "official" }))
    .map(([code, name]) => [code, name]);

  // Preselect country based on user's settings or default to Mexico
  const defaultCountry = currentUser?.calls_country || "MX";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
  };

  const handleSave = async () => {
    try {
      await updateUserSettings({ calls_country: selectedCountry });
      toast.success(t('call_empty_state.fields.settings_updated'));
      setIsModalOpen(false);
      if (onCountrySelected) {
        onCountrySelected();
      }
    } catch (error) {
      toast.error(t('call_empty_state.fields.settings_update_failed'));
    }
  };

  return (
    <>
      {asLink ? (
        <LinkButton
          onClick={handleOpenModal}
          data-testid="calls-country-link"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <GlobalOutlined />
          <span style={{ marginRight: '8px' }}>{t('call_empty_state.fields.select_country_as_link')}</span>
          <span className={`fi fi-${currentUser?.calls_country?.toLowerCase()}`} style={{ fontSize: '1.2em' }}></span>
        </LinkButton>
      ) : (
        <Button
          type="primary"
          size="large"
          shape="round"
          onClick={handleOpenModal}
          data-testid="try-calls-button"
        >
          {t('call_empty_state.fields.try_now')}
        </Button>
      )}
      <Modal
        title={t('call_empty_state.fields.select_country')}
        open={isModalOpen}
        onOk={handleSave}
        onCancel={handleCloseModal}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: '1.5rem' }}>
          {t('call_empty_state.fields.select_country_subtitle')}
        </Text>
        <Select
          style={{ width: '100%', marginBottom: '1rem', fontSize: '16px' }}
          onChange={handleCountryChange}
          value={selectedCountry}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
        >
          {countryOptions.map(([code, name]) => (
            <Option key={code} value={code} label={name}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className={`fi fi-${code.toLowerCase()}`} style={{ fontSize: '1.2em' }}></span>
                <span>{name}</span>
              </div>
            </Option>
          ))}
        </Select>
      </Modal>
    </>
  );
};

export default CallsCountrySelector;
